import { useTranslation } from 'react-i18next';
import type { CiteArretQualifie } from './types';
import { CITE_TYPE_CONFIG } from './citeTypeConfig';

interface CiteArretCardProps {
  cite: CiteArretQualifie;
}

export const CiteArretCard = ({ cite }: CiteArretCardProps) => {
  const { t } = useTranslation();
  const cfg = CITE_TYPE_CONFIG[cite.type] || CITE_TYPE_CONFIG.obiter_dictum;

  const inner = (
    <div className={`p-4 rounded-xl border text-sm transition-all ${
      cite.found ? 'bg-card hover:border-primary/30 hover:shadow-sm' : 'bg-muted/30'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">

          {/* Ref + type + citation_count */}
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className="font-mono text-xs text-muted-foreground">{cite.ref}</span>
            <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${cfg.color}`}>
              {cfg.icon} {t(cfg.labelKey)}
            </span>
            {cite.citation_count > 0 && (
              <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-1.5 py-0.5 rounded font-mono">
                ★ {cite.citation_count}
              </span>
            )}
          </div>

          {/* Contexte */}
          {cite.contexte && (
            <p className="text-xs text-muted-foreground italic mb-2 leading-relaxed">
              "{cite.contexte}"
            </p>
          )}

          {/* Fiche trouvée */}
          {cite.found ? (
            <>
              <p className="font-medium text-xs text-foreground">
                {cite.chamber} — {cite.decision_date}
              </p>
              {cite.probleme && (
                <p className="text-muted-foreground mt-1 line-clamp-2 text-xs leading-relaxed">
                  {cite.probleme}
                </p>
              )}
            </>
          ) : (
            <p className="text-xs text-muted-foreground/60 italic">
              {t('fiche.cite_card.not_indexed')}
            </p>
          )}
        </div>

        {cite.found && cite.fiche_id && (
          <svg className="w-4 h-4 shrink-0 text-muted-foreground mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        )}
      </div>
    </div>
  );

  // cite.slug contient le chemin de route (ex: cour-de-cassation/chambre-crim/2021-12-01/20-83.235)
  // cite.fiche_id est l'UUID Qdrant — ne pas l'utiliser comme URL
  if (cite.found && cite.slug) {
    return (
      <a href={`/jurisprudence/${cite.slug}`} target="_blank" rel="noopener noreferrer" className="block">
        {inner}
      </a>
    );
  }

  return inner;
};